// `extension-view.service.ng1.ts` + `shortlist.view-transition.service.ng1.ts` — how wide the
// injected panel is for the screen that is open.
//
// ⚠️ THE SPA DRIVES THE PANEL'S WIDTH, AND THIS PORT NEVER TOLD IT. The shell has handled
// `SetViewModeStarted` since 6.3 (`shortlist-handlers.ts`); nothing in the SPA ever sent one,
// so the panel stayed at its stored width whatever screen was open. Opening a candidate or the
// edit-alert screen left a 1146px-wide form inside a 200px frame.
//
// ⚠️ AND THE TABLE IS TRANSCRIBED, BECAUSE THE EXTRACTOR CANNOT SEE IT. Every `viewWidth:` in
// `extension.state.tabs.config.ng1.ts` is written as `ExtensionViewWidth.Slim +
// ExtensionViewWidth.Extended` — enum arithmetic, which `tools/extract` records as
// `"<computed>"` in routes.json, exactly as it does `privAccess`. So the numbers live here,
// beside their paths, the way `extensionChrome.ts` keeps the tab table for the same reason.
import { ExtensionViewWidth } from '@woo-ext/protocol'

const { Slim, Extended, Detailed, Full } = ExtensionViewWidth

/**
 * Each route's OWN `data.viewWidth`. The effective width is the maximum over the route and
 * its ancestors — see `maxViewWidthFor`.
 *
 * ⚠️ A ROUTE MISSING FROM HERE IS NOT WIDTH ZERO, it is "declares nothing", which is a real
 * state: `getMaxViewWidth` walks past such a state to its parent, and a chain that declares
 * nothing at all sends no message and leaves the panel as it was.
 */
const VIEW_WIDTHS: Readonly<Record<string, number>> = {
  // ── candidates ──────────────────────────────────────────────────────────────────────────
  '/chrome-extension/extension/shortlist': Slim,
  '/chrome-extension/extension/shortlist/edit-alert': Full,
  '/chrome-extension/extension/shortlist/review': Slim,
  '/chrome-extension/extension/shortlist/review/email-signin': Slim + Extended,
  '/chrome-extension/extension/shortlist/review/candidate-messages': Slim + Detailed,
  '/chrome-extension/extension/shortlist/review/add-to-job': Slim + Extended,
  '/chrome-extension/extension/shortlist/review/export': Slim + Extended,
  '/chrome-extension/extension/shortlist/review/candidate/profile': Slim + Extended,
  '/chrome-extension/extension/shortlist/review/candidate/profile/more': Slim + Extended + Detailed,
  '/chrome-extension/extension/shortlist/profile-not-found': Extended,
  '/chrome-extension/extension/shortlist/candidate/profile': Extended,
  '/chrome-extension/extension/shortlist/candidate/profile/more': Extended + Detailed,
  '/chrome-extension/extension/shortlist/candidate/profile/add-to-job': Extended + Extended,
  '/chrome-extension/extension/shortlist/candidate/profile/email-signin': Extended + Extended,
  '/chrome-extension/extension/shortlist/candidate/profile/export': Extended + Extended,
  '/chrome-extension/extension/shortlist/candidate/profile/candidate-messages': Extended + Detailed,
  '/chrome-extension/extension/shortlist/external-source-review': Slim,
  '/chrome-extension/extension/shortlist/external-source-review/candidate/external-source-profile':
    Slim + Extended + Detailed,
  '/chrome-extension/extension/shortlist/external-source-review/external-source-add-to-job': Slim + Extended,

  // ── contacts ────────────────────────────────────────────────────────────────────────────
  '/chrome-extension/extension/client-contact': Slim,
  '/chrome-extension/extension/client-contact/edit-alert': Full,
  '/chrome-extension/extension/client-contact/review': Slim,
  '/chrome-extension/extension/client-contact/review/email-signin': Slim + Extended,
  '/chrome-extension/extension/client-contact/review/client-contact-messages': Slim + Detailed,
  '/chrome-extension/extension/client-contact/review/export': Slim + Extended,
  '/chrome-extension/extension/client-contact/review/client-contact/profile': Slim + Extended,
  '/chrome-extension/extension/client-contact/review/client-contact/profile/more':
    Slim + Extended + Detailed,
  '/chrome-extension/extension/client-contact/profile-not-found': Extended,
  '/chrome-extension/extension/client-contact/client-contact/profile': Extended,
  '/chrome-extension/extension/client-contact/client-contact/profile/more': Extended + Detailed,
  '/chrome-extension/extension/client-contact/client-contact/profile/email-signin': Extended + Extended,
  '/chrome-extension/extension/client-contact/client-contact/profile/export': Extended + Extended,
  '/chrome-extension/extension/client-contact/client-contact/profile/client-contact-messages':
    Extended + Detailed,
}

/**
 * `getMaxViewWidth(state)` — the largest width this route or any ancestor declares.
 *
 * ⚠️ THE MAXIMUM, NOT THE LEAF'S OWN. A child that declares less than its parent does not
 * shrink the panel: `/review/candidate/profile` is 578 and `/review` is 200, and entering the
 * child widens; leaving it narrows again because the parent is then the deepest match. The
 * path prefix IS the state chain here, the same equivalence `guardsFor` relies on.
 */
export function maxViewWidthFor(pathname: string): number | undefined {
  let max: number | undefined
  for (const [path, width] of Object.entries(VIEW_WIDTHS)) {
    if (pathname !== path && !pathname.startsWith(`${path}/`)) continue
    if (max === undefined || width > max) max = width
  }
  return max
}

export { VIEW_WIDTHS as VIEW_WIDTHS_FOR_TEST }
